import isNil from 'lodash/isNil';
import sanitizeHtml from 'sanitize-html';
import { http } from '@pendo/http';
import { PendoNotification } from '@pendo/components';
import { i18n_t as $t } from '@/utils/i18n';
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';

export const getStateOptions = () =>
    Object.freeze({
        verified: {
            state: 'verified',
            label: $t('Verified'),
            icon: { type: 'shield-check', fill: 'var(--palette-green-10)', stroke: 'var(--palette-green-80)', size: 16 },
        },
        unverified: {
            state: 'unverified',
            label: $t('Unverified'),
            icon: { type: 'shield', fill: 'var(--palette-gray-20)', stroke: 'var(--palette-gray-70)', size: 16 },
        },
        requested: {
            state: 'requested',
            label: $t('Requested'),
            icon: { type: 'shield-alert', fill: 'var(--palette-blue-10)', stroke: 'var(--palette-blue-70)', size: 16 },
        },
        rejected: {
            state: 'rejected',
            label: $t('Rejected'),
            icon: { type: 'shield-x', fill: 'var(--palette-red-10)', stroke: 'var(--palette-red-70)', size: 16 },
        },
    });

export const STATE_OPTIONS = getStateOptions();

export const getActionOptions = () =>
    Object.freeze({
        accept: { state: 'accept', label: $t('Accept and verify') },
        request: { state: 'request', label: $t('Request verification') },
        reject: { state: 'reject', label: $t('Reject') },
        cancel: { state: 'cancel', label: $t('Cancel request'), color: 'var(--palette-red-70)' },
        view: { state: 'view', label: $t('View request note') },
    });

export const ACTION_OPTIONS = getActionOptions();

export const ACTION_STATE_MAP = Object.freeze({
    verify: 'verified',
    accept: 'verified',
    unverify: 'unverified',
    request: 'requested',
    rerequest: 'requested',
    reject: 'rejected',
    cancel: 'unverified',
    view: 'requested',
});

export const STATE_ACTION_MAP = Object.freeze({
    verified: 'verify',
    unverified: 'unverify',
    requested: 'request',
    rejected: 'reject',
});

/* eslint-disable internal-rules/no-hardcoded-colors */
export const STATUS_COLOR_MAP = Object.freeze({
    verified: 'green',
    verify: 'green',
    accept: 'green',
    unverified: 'gray',
    unverify: 'gray',
    requested: 'teal',
    request: 'teal',
    rejected: 'red',
    reject: 'red',
});
/* eslint-enable internal-rules/no-hardcoded-colors */

export function showToastMessage (status, entityName, error) {
    const errorMessage = error?.response?.data?.trim() || 'An error occurred while updating the verification status';
    const { title, message } = getToastMessage(status, sanitizeHtml(entityName, { allowedTags: [], disallowedTagsMode: 'escape' }), error ? errorMessage : undefined);

    PendoNotification({
        type: error ? 'error' : 'success',
        duration: 4000,
        title,
        dangerouslyUseHTMLString: true,
        message,
    });
}

export function getToastMessage (action, entityName = '', errorMessage = '') {
    const type = errorMessage ? 'error' : 'success';

    return {
        verified: {
            success: {
                title: $t('Verification successful'),
                message: $t('verificationStatusUtils.toastMessage.VerificationSuccessful', {
                    entityName: `<b>${entityName}</b>`,
                }),
            },
            error: {
                title: $t('Verification failed'),
                message: $t('verificationStatusUtils.toastMessage.VerificationFailed', {
                    entityName: `<b>${entityName}</b>`,
                    errorMessage,
                }),
            },
        },
        unverified: {
            success: {
                title: $t('Unverification successful'),
                message: $t('verificationStatusUtils.toastMessage.UnverificationSuccessful', {
                    entityName: `<b>${entityName}</b>`,
                }),
            },
            error: {
                title: $t('Unverification failed'),
                message: $t('verificationStatusUtils.toastMessage.UnverificationFailed', {
                    entityName: `<b>${entityName}</b>`,
                    errorMessage,
                }),
            },
        },
        requested: {
            success: {
                title: $t('Verification request sent'),
                message: $t('verificationStatusUtils.toastMessage.RequestSuccessful', {
                    entityName: `<b>${entityName}</b>`,
                }),
            },
            error: {
                title: $t('Verification request failed'),
                message: $t('verificationStatusUtils.toastMessage.RequestFailed', {
                    entityName: `<b>${entityName}</b>`,
                    errorMessage,
                }),
            },
        },
        rejected: {
            success: {
                title: $t('Verification request rejected'),
                message: $t('verificationStatusUtils.toastMessage.RejectionSuccessful', {
                    entityName: `<b>${entityName}</b>`,
                }),
            },
            error: {
                title: $t('Verification rejection failed'),
                message: $t('verificationStatusUtils.toastMessage.RejectionFailed', {
                    entityName: `<b>${entityName}</b>`,
                    errorMessage,
                }),
            },
        },
    }[action][type];
}

export function formatUserName ({ first, last, username }) {
    if (first && last) {
        return `${first} ${last}`;
    }

    return username;
}

export function tooltipContent ({ state, lastUpdatedAt, lastUpdatedByUser = {} }, displayName) {
    const value = (state || 'unverified').toLowerCase();
    const updatedBy = displayName || formatUserName(lastUpdatedByUser);

    if (value === 'unverified' || (!updatedBy && isNil(lastUpdatedAt))) {
        return '';
    }

    const tooltipMappings = {
        verified: {
            state: $t('Verified'),
            stateBy: $t('verificationStatusUtils.tooltipContent.Verified by {name}', { name: updatedBy }),
            stateDate: $t('verificationStatusUtils.tooltipContent.Verified on {date}', {
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
            stateByDate: $t('verificationStatusUtils.tooltipContent.Verified by {name} on {date}', {
                name: updatedBy,
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
        },
        unverified: {
            state: $t('Unverified'),
            stateBy: $t('verificationStatusUtils.tooltipContent.Unverified by {name}', { name: updatedBy }),
            stateDate: $t('verificationStatusUtils.tooltipContent.Unverified on {date}', {
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
            stateByDate: $t('verificationStatusUtils.tooltipContent.Unverified by {name} on {date}', {
                name: updatedBy,
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
        },
        rejected: {
            state: $t('Rejected'),
            stateBy: $t('verificationStatusUtils.tooltipContent.Rejected by {name}', { name: updatedBy }),
            stateDate: $t('verificationStatusUtils.tooltipContent.Rejected on {date}', {
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
            stateByDate: $t('verificationStatusUtils.tooltipContent.Rejected by {name} on {date}', {
                name: updatedBy,
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
        },
        requested: {
            state: $t('Requested'),
            stateBy: $t('verificationStatusUtils.tooltipContent.Requested by {name}', { name: updatedBy }),
            stateDate: $t('verificationStatusUtils.tooltipContent.Requested on {date}', {
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
            stateByDate: $t('verificationStatusUtils.tooltipContent.Requested by {name} on {date}', {
                name: updatedBy,
                date: i18nDateTimeFormatter.formatFullDate({ date: lastUpdatedAt }),
            }),
        },
    };

    if (updatedBy && !isNil(lastUpdatedAt)) {
        return tooltipMappings[value].stateByDate;
    }

    if (updatedBy) {
        return tooltipMappings[value].stateBy;
    }

    if (!isNil(lastUpdatedAt)) {
        return tooltipMappings[value].stateDate;
    }

    return tooltipMappings[value].state;
}

export function updateVerificationState ({ kind, id, state, notes }) {
    const payload = {
        state,
        notes,
    };

    return http.put(`/api/s/_SID_/verification/${kind}/${id}`, payload);
}

export function readOnlyVerification (verification = {}, config = {}) {
    const { shared, userId, canVerify } = config;
    const verificationState = (verification.state || 'Unverified').toLowerCase();

    // Verification is useless for private entities.
    // Allow the user to cancel/exit other verification statuses,
    // but don't allow them to re-enter those states when the entity is private.
    if (!shared && verificationState === 'unverified') {
        return true;
    }

    const requestAuthor = verification.lastUpdatedByUser;
    const didNotAuthorRequest = requestAuthor?.id !== userId;

    return !canVerify && didNotAuthorRequest && verificationState === 'requested';
}
